import { HTTPClientInterface, HTTPRequest } from '@shared/core/HTTPClientInterface';

import { buildURL, SystemURL } from "./URLCatalog";
import { PermissionRecord } from "@models/Permissions";

// User creation interface
export interface CreateUserRequest {
    name: string;
    phoneNumber: string;
    email?: string;
    expirationDate: number;
}

// File upload interfaces
export interface GenerateUploadUrlRequest {
    originalName: string;
    contentType: string;
}

export interface LinkDocumentRequest {
    publicURL: string;
    originalName: string;
    contentType: string;
}

export interface DeleteDocumentRequest {
    document_id: string;
    document_name: string;
}

export interface PresenteeismTableRequest {
    yearMonth: string;
}

export interface CreateAdminUserRequest {
    name: string;
    mail: string;
}


interface FirebaseUser {
    email: string;
    id: string;
    image: string;
    isCreatedByFirebaseAuth: boolean;
    lastLoginDate: number;
    name: string;
}

export interface UserSearchResult {
    objectID: string;
    id: string;
    name: string;
    email?: string;
    image?: string;
    isListableUser: boolean;
    organizationIDs: string[];
    userIDReference?: string;
    mobilePhone?: string;
    dni?: string;
    empresa?: string;
    address?: string;
    localidad?: string;
    legajo?: string;
    birthDate?: string;
    jobTitle?: string;
    imageURL?: string;
}

export interface UserSearchPage {
    hits: UserSearchResult[];
    nbHits: number;
    page: number;
    nbPages: number;
}

export interface AuthenticatedDashboardUser {
    id: string;
    name?: string;
    email?: string;
    image?: string;
    permissions?: PermissionRecord[];
    isSystemAdmin?: boolean;
}

export class UsersService {
    private httpClient: HTTPClientInterface;

    constructor(httpClient: HTTPClientInterface) {
        this.httpClient = httpClient;
    }

    async createUser(orgId: string, userData: CreateUserRequest) {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Users.postCreateUser, { org_id: orgId });
        request.method = "POST";
        request.body = userData;
        return this.httpClient.execute(request);
    }

    async getAuthenticatedUser(): Promise<AuthenticatedDashboardUser> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Users.postUser, {});
        request.method = "POST";
        const response = await this.httpClient.execute(request);
        return response.result as AuthenticatedDashboardUser;
    }

    async uploadPhoto(userId: string, orgId: string, formData: FormData) {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Users.postUploadPhoto, { user_id: userId, org_id: orgId });
        request.method = "POST";
        request.body = formData;
        return this.httpClient.execute(request);
    }

    async editUser(orgId: string, userId: string, editData: any) {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Users.postEdit, { org_id: orgId, user_id: userId });
        request.method = "POST";
        request.body = editData;
        return this.httpClient.execute(request);
    }

    async deactivateUser(orgId: string, userId: string) {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Users.postDeactivate, { org_id: orgId, user_id: userId });
        request.method = "POST";
        return this.httpClient.execute(request);
    }

    async reactivateUser(orgId: string, userId: string) {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Users.postReactivate, { org_id: orgId, user_id: userId });
        request.method = "POST";
        return this.httpClient.execute(request);
    }

    async getDocuments(userId: string, orgId: string) {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Users.getDocuments, { user_id: userId, org_id: orgId });
        request.method = "GET";
        const response = await this.httpClient.execute(request);
        return response.result;
    }

    async generateUploadUrl(userId: string, uploadData: GenerateUploadUrlRequest) {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Users.postGenerateUploadUrl, { user_id: userId });
        request.method = "POST";
        request.body = uploadData;
        const response = await this.httpClient.execute(request);
        return response.result;
    }

    async linkDocument(userId: string, orgId: string, documentData: LinkDocumentRequest) {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Users.postLinkDocument, { user_id: userId, org_id: orgId });
        request.method = "POST";
        request.body = documentData;
        return this.httpClient.execute(request);
    }

    async getDownloadDocument(userId: string, fileUrl: string) {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Users.getDownloadDocument, { user_id: userId, fileUrl });
        request.method = "GET";
        request.responseType = "blob";
        return this.httpClient.execute(request);
    }

    async deleteDocument(userId: string, orgId: string, documentData: DeleteDocumentRequest) {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Users.deleteDocument, { user_id: userId, org_id: orgId });
        request.method = "DELETE";
        request.body = documentData;
        return this.httpClient.execute(request);
    }

    async getNomina(orgId: string) {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Users.getNomina, { org_id: orgId });
        request.method = "GET";
        const response = await this.httpClient.execute(request);
        return response.result;
    }

    async searchUsers(orgId: string, query: string, page = 0, hitsPerPage = 20): Promise<UserSearchPage> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Users.searchUsers, {
            org_id: orgId,
            query,
            page: String(page),
            hitsPerPage: String(hitsPerPage),
        });
        request.method = "GET";
        const response = await this.httpClient.execute(request);
        const result = response.result ?? {};
        return {
            hits: (result.hits ?? []) as UserSearchResult[],
            nbHits: result.nbHits ?? 0,
            page: result.page ?? page,
            nbPages: result.nbPages ?? 0,
        };
    }

    async getWhatsappPresenteeismTable(phone: string, orgId: string, tableRequest: PresenteeismTableRequest) {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Users.getWhatsappPresenteeismTable, { phone, org_id: orgId });
        request.method = "POST";
        request.body = tableRequest;
        const response = await this.httpClient.execute(request);
        return response.result
    }

    async getAdminUsers(orgId: string): Promise<FirebaseUser[]> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Users.getAdminUsers, { org_id: orgId });
        request.method = "GET";
        const response = await this.httpClient.execute(request);
        return response.result as FirebaseUser[];
    }

    async inviteAdminUser(orgId: string, adminData: {name: string; email: string}) {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Users.postCreateAdminUser, {org_id: orgId});
        request.method = "POST";
        request.body = adminData;
        await this.httpClient.execute(request);
    }

    async deactivateAdminUser(orgId: string, userId: string) {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Users.deactivateAdminUser, {org_id: orgId, user_id: userId});
        request.method = "DELETE";
        await this.httpClient.execute(request);
    }
}
